import React, { useState, useEffect, useRef } from 'react';
import MapDisplay from '../map/MapDisplay';
import useIntersectionObserver from '../../hooks/useIntersectionObserver';
import mapPreloader from '../../utils/mapPreloader';
import './LazyMapDisplay.css';

/**
 * LazyMapDisplay - Only renders MapDisplay when the component is visible
 * This prevents Mapbox from creating unnecessary map instances
 */
const LazyMapDisplay = ({ 
  location, 
  className = '', 
  zoom = 12,
  // bottom sheet passthrough
  bottomSheetEnabled,
  bottomSheetExpanded,
  onBottomSheetToggle,
  onBottomSheetCollapse,
  bottomSheetCollapsedHeight,
  bottomSheetExpandedHeight,
  bottomSheetTitle,
  bottomSheetSubtitle,
  onBottomSheetPrimary,
  bottomSheetPrimaryLabel,
  bottomSheetContent,
  // directions passthrough
  directions,
  directionsProfile,
  onDirectionsInfo
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [ref, isIntersecting] = useIntersectionObserver({
    rootMargin: '300px 0px' // Start loading 300px before visible
  });

  // Only render the map when it's about to become visible
  useEffect(() => {
    if (isIntersecting && !shouldRender) {
      // Start preloading map resources when component becomes visible
      mapPreloader.preloadMapResources();
      
      // Small delay to allow preloading to start
      setTimeout(() => {
        setShouldRender(true);
      }, 100);
    }
  }, [isIntersecting, shouldRender]);

  return (
    <div ref={ref} className={`lazy-map-container ${className}`}>
      {shouldRender ? (
        <MapDisplay 
          location={location} 
          className={className} 
          zoom={zoom}
          bottomSheetEnabled={bottomSheetEnabled}
          bottomSheetExpanded={bottomSheetExpanded}
          onBottomSheetToggle={onBottomSheetToggle}
          onBottomSheetCollapse={onBottomSheetCollapse}
          bottomSheetCollapsedHeight={bottomSheetCollapsedHeight}
          bottomSheetExpandedHeight={bottomSheetExpandedHeight}
          bottomSheetTitle={bottomSheetTitle}
          bottomSheetSubtitle={bottomSheetSubtitle}
          onBottomSheetPrimary={onBottomSheetPrimary}
          bottomSheetPrimaryLabel={bottomSheetPrimaryLabel}
          bottomSheetContent={bottomSheetContent}
          directions={directions}
          directionsProfile={directionsProfile}
          onDirectionsInfo={onDirectionsInfo}
        />
      ) : (
        <div className="map-placeholder">
          <div className="map-loading-indicator">
            <div className="loading-spinner"></div>
            <span>Loading map...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyMapDisplay;
